import { ValidateIf } from 'class-validator';
import { PartialType } from 'nestjs-mapped-types';
import { Enum, field } from '../../fields';
import {
	BrandId,
	FragranceId,
	FragranceLinkId,
	FragrancePhotoId,
	IngredientId,
	NoteId,
	OlfactiveFamilyId,
	PerfumerId,
	StoreId,
	UserId,
} from '../id';
import {
	ComplaintType,
	Concentration,
	FragranceModerationStatus,
	FragrancePhotoStatus,
	Gender,
	Longevity,
	ModerationStatus,
	Occasion,
	Season,
	Sillage,
	Weather,
} from '../model.enums';

export const FragranceLinkType = Enum('ARTICLE', 'VIDEO', 'INTERNAL');
export type FragranceLinkType = Enum<typeof FragranceLinkType>;

export const ASINType = Enum('PRIMARY', 'VARIATION', 'MANUAL', 'POTENTIAL', 'BLACKLIST');
export type ASINType = Enum<typeof ASINType>;

export class FragrancePhotoDto {
	@FragrancePhotoId({ optional: true })
	id?: FragrancePhotoId;

	@field.String({ optional: true })
	authorName?: string;

	@UserId({ optional: true })
	authorId?: UserId;

	@field.String({ optional: true })
	caption?: string;

	@field.String()
	@ValidateIf(o => !!o.id)
	fileId!: string;

	@field.String()
	url!: string;

	@field.String({ optional: true })
	authorUser?: string;

	@field.String({ optional: true })
	complainedByUser?: string;

	@field.Enum({ ComplaintType }, { optional: true })
	complaintStatus?: ComplaintType;
}

export class FragranceLinkDto {
	@FragranceLinkId({ optional: true })
	id?: FragranceLinkId;

	@field.String({ min: 1, max: 200 })
	title!: string;

	@field.String({ max: 200 })
	subtitle!: string;

	@field.Url()
	url!: string;

	@field.Enum({ FragranceLinkType })
	type!: FragranceLinkType;
}

export class FragranceOfferDto {
	@StoreId()
	storeId!: StoreId;

	@field.String()
	currency!: string;

	@field.Number()
	price!: number;

	@field.Number({ optional: true, nullable: true })
	discountPrice?: number | null;

	@field.Bool()
	available!: boolean;

	@field.Url()
	buyUrl!: string;

	@field.String({ optional: true, nullable: true, max: 64, trim: true })
	title?: string | null;
}

export class FragranceASINDto {
	@field.Number({ optional: true })
	id?: number;

	@field.Enum({ ASINType })
	type!: ASINType;

	@field.String()
	asin!: string;
}

export class FragranceUpdateDto {
	@field.String({ min: 1, max: 256 })
	name!: string;

	@BrandId()
	brandId!: BrandId;

	@field.String({ optional: true })
	description?: string;

	@field.String({ array: true, optional: true })
	synonyms?: string[];

	@field.Obj(FragrancePhotoDto, { array: true, optional: true })
	publicPhotos?: FragrancePhotoDto[];

	@field.Obj(FragrancePhotoDto, { array: true, optional: true })
	privatePhotos?: FragrancePhotoDto[];

	@field.Obj(FragrancePhotoDto, { array: true, optional: true })
	hiddenPhotos?: FragrancePhotoDto[];

	@field.Obj(FragrancePhotoDto, { array: true, optional: true })
	preliminaryPhotos?: FragrancePhotoDto[];

	@field.Number({ optional: true, array: true, int: true, min: 1800, max: 2500 })
	years?: number[];

	@PerfumerId({ array: true, optional: true })
	perfumers?: PerfumerId[];

	@OlfactiveFamilyId({ array: true, optional: true })
	olfactives?: OlfactiveFamilyId[];

	@field.String({ optional: true })
	comment?: string;

	@field.Bool({ optional: true })
	active?: boolean;

	@field.Bool({ optional: true })
	discontinued?: boolean;

	@NoteId({ array: true, optional: true })
	brandTopNotes?: NoteId[];

	@NoteId({ array: true, optional: true })
	brandMidNotes?: NoteId[];

	@NoteId({ array: true, optional: true })
	brandBaseNotes?: NoteId[];

	@NoteId({ array: true, optional: true })
	noseNotes?: NoteId[];

	@IngredientId({ array: true, optional: true })
	ingredients?: IngredientId[];

	@field.Enum({ Gender }, { maybe: true })
	gender?: Gender;

	@field.Enum({ Sillage }, { maybe: true })
	sillage?: Sillage;

	@field.Enum({ Longevity }, { maybe: true })
	longevity?: Longevity;

	@field.Enum({ Sillage }, { maybe: true })
	noseSillage?: Sillage;

	@field.Enum({ Longevity }, { maybe: true })
	noseLongevity?: Longevity;

	@field.Enum({ Concentration }, { maybe: true })
	noseConcentration?: Concentration;

	@field.Enum({ Concentration }, { maybe: true })
	concentration?: Concentration;

	@field.Enum({ Occasion }, { maybe: true, array: true })
	noseOccasion?: Occasion[];

	@field.Enum({ Weather }, { maybe: true, array: true })
	noseWeather?: Weather[];

	@field.Enum({ Season }, { maybe: true, array: true })
	noseSeason?: Season[];

	@FragranceId({ array: true, optional: true })
	noseRemindsFragrances?: FragranceId[];

	@field.Obj(FragranceLinkDto, { array: true })
	links: FragranceLinkDto[] = [];

	@field.Obj(FragranceOfferDto, { array: true })
	offers: FragranceOfferDto[] = [];

	@field.Obj(FragranceASINDto, { array: true })
	asin: FragranceASINDto[] = [];
}

export class FragranceCreateDto extends FragranceUpdateDto {
	@FragranceId({ optional: true })
	id?: FragranceId;
}

export class FragranceModerationDto {
	@field.Enum({ FragranceModerationStatus })
	status!: FragranceModerationStatus;

	@field.String({ optional: true })
	description?: string;
}

export class FragranceImportDto extends PartialType(FragranceCreateDto) {}

export class FragranceImport {
	@field.Obj(FragranceImportDto, { array: true })
	items!: FragranceImportDto[];

	@field.String()
	fileName!: string;
}

export class FragrancePhotoImportDto {
	@FragranceId()
	fragranceId!: FragranceId;

	@field.String({ min: 1 })
	fileId!: string;

	@field.Enum({ FragrancePhotoStatus })
	status!: FragrancePhotoStatus;
}

export class FragrancePhotoImport {
	@field.Obj(FragrancePhotoImportDto, { array: true })
	items!: FragrancePhotoImportDto[];

	@field.String()
	fileName!: string;
}

export class FragranceSetSuperDto {
	@FragranceId({ nullable: true })
	superFragranceId!: FragranceId | null;
}

export class FragranceChangePhotoComplaintDto {
	@field.Bool()
	clear!: boolean;

	@field.String()
	fileId!: string;
}

export class FragranceReviewStatusDto {
	@field.Enum({ ModerationStatus })
	status!: ModerationStatus;
}

export class FragranceListFilterFieldsDto {
	@BrandId({ optional: true })
	brandId?: BrandId;

	@field.Number({ optional: true, min: 0, max: 1 })
	active?: 0 | 1;

	@field.Enum({ FragranceModerationStatus }, { optional: true })
	moderationStatus?: FragranceModerationStatus;
}
